import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { BaseTextField } from '../../BaseTextField/BaseTextField';
import { SelectInputValues } from './SelectInputValues';
import {
  ISelectOption,
  PSelect,
  selectValuesType,
  TSelectOptions,
} from '../common/types';
import { SelectLoader } from './SelectLoader';
import { ITEM_HEIGHT, ITEM_PADDING_TOP } from '../common/helpers';
import { SelectMenu } from '../common/SelectMenu/SelectMenu';
import { MultiSelectOption } from './MultiSelectOption';

const useStyles = makeStyles(() => ({
  inputStyle: {
    '& .MuiInputBase-input': {
      paddingTop: '9px',
      paddingBottom: '9px',
    },
  },
}));

const getObjOptions = (optionsList: Array<ISelectOption>) => {
  const optionsObj = {};
  optionsList.forEach((option) => {
    // @ts-ignore
    optionsObj[option.value] = option;
  });
  return optionsObj;
};

export type PBaseMultiSelect = PSelect<selectValuesType>;

const MENU_BASE_PROPS = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
  getContentAnchorEl: null,
  PaperProps: {
    style: {
      paddingTop: ITEM_PADDING_TOP,
      maxHeight: ITEM_HEIGHT * 8 + ITEM_PADDING_TOP,
    },
  },
};

export const BaseMultiSelect: React.FC<PBaseMultiSelect> = (props) => {
  const {
    value,
    options,
    placeholder,
    label,
    loading,
    onChange,
    errorText,
    ...otherProps
  } = props;
  const classes = useStyles();

  // Create obj for optimization. We get option object without additional loops
  // In this case we prevent O(n2) algorithmic efficiency
  const objOptions: any = useMemo(() => getObjOptions(options), [options]);

  const [localValue, setValue] = useState<Array<ISelectOption>>([]);

  const searchInputRef = useRef<React.Ref<any>>();

  const MenuProps = {
    MenuListProps: {
      component: SelectMenu,
      selectMenuData: {
        OptionComponent: MultiSelectOption,
        onClick: (newValue: selectValuesType) => {
          onChange(newValue);
        },
        selected: value,
        inputRef: searchInputRef,
        options,
      },
    },
    ...MENU_BASE_PROPS,
  };

  useEffect(() => {
    // Find option for each value
    const foundOptions: TSelectOptions = [];
    (value || []).forEach((el) => {
      if (objOptions[el] !== undefined) {
        foundOptions.push(objOptions[el]);
      }
    });
    setValue(foundOptions);
  }, [value, objOptions]);
  const [open, selectHandleOnOpen] = React.useState(false);

  useEffect(() => {
    // In this case I use setTimeout because searchInputRef is not set upped when
    // open === true
    setTimeout(() => {
      if (open && searchInputRef.current) {
        // @ts-ignore
        searchInputRef?.current.focus();
      }
    });
  }, [open]);

  return (
    <FormControl {...otherProps}>
      <Select
        // @ts-ignore
        MenuProps={MenuProps}
        style={{ width: '100%' }}
        multiple
        value={value}
        open={open}
        onClick={() => selectHandleOnOpen((prev) => !prev)}
        input={
          <BaseTextField
            className={classes.inputStyle}
            label={label}
            placeholder={placeholder}
            onClick={(e) => {
              // Stop opening select
              e.stopPropagation();
            }}
            InputProps={{
              endAdornment: <>{loading ? <SelectLoader /> : null}</>,
            }}
            errorText={errorText}
          />
        }
        renderValue={() => {
          return (
            <SelectInputValues
              selected={localValue}
              onRemove={(e: any, el: ISelectOption) => {
                // Stop opening select
                e.stopPropagation();
                const newValue = value.filter(
                  (currentValue) => currentValue !== el.value,
                );
                onChange(newValue);
              }}
            />
          );
        }}
      />
    </FormControl>
  );
};
